import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { useCart } from "../../lib/useCart";
import { ChevronLeft, ChevronRight, Star, ShoppingCart, Package, AlertCircle, ArrowLeft, Play, Sparkles } from "lucide-react";
import { Icon } from "../../lib/icons";
import { toast } from "sonner";
import WishlistButton from "../../components/public/WishlistButton";
import { useWishlist } from "../../lib/useWishlist";

function priceFmt(cents, symbol = "€") {
  return `${(cents / 100).toFixed(2)} ${symbol}`;
}

function extractYoutubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/ ]{11})/);
  return m ? m[1] : null;
}

export default function ProductPage() {
  const { slug } = useParams();
  const { settings: s } = useSettings();
  const { add: addToCart } = useCart();
  const { daysSinceFavorited } = useWishlist();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [qty, setQty] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({});

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/shop/products/${slug}`)
      .then(r => {
        if (!alive) return;
        setP(r.data);
        setIdx(0);
        setShowVideo(false);
        // Initialise les options sélectionnées si produit variable
        if (r.data?.product_type === "variable" && Array.isArray(r.data.variants) && r.data.variants.length > 0) {
          const def = r.data.variants.find(v => v.is_default) || r.data.variants[0];
          setSelectedOptions({ ...(def?.options || {}) });
        } else {
          setSelectedOptions({});
        }
      })
      .catch(() => { if (alive) setP(false); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  const isVariable = !!(p && p.product_type === "variable" && Array.isArray(p.variants) && p.variants.length > 0);
  const activeVariant = React.useMemo(() => {
    if (!isVariable || !p) return null;
    return (p.variants || []).find(v => {
      const opts = v.options || {};
      return Object.keys(opts).every(k => opts[k] === selectedOptions[k]);
    }) || null;
  }, [isVariable, p, selectedOptions]);

  if (loading) return <div className="py-24 text-center text-[hsl(var(--muted-foreground))]">Chargement…</div>;
  if (!p) return (
    <section className="py-24 text-center">
      <Package className="w-12 h-12 mx-auto text-[hsl(var(--muted-foreground))]/30 mb-3" />
      <p className="text-[hsl(var(--muted-foreground))]">Produit introuvable.</p>
      <Link to="/shop" className="inline-flex items-center gap-1.5 mt-4 text-[color:var(--brand-sky)] hover:underline">
        <ArrowLeft className="w-4 h-4" />Retour à la boutique
      </Link>
    </section>
  );

  const images = [p.featured_image, ...(p.gallery || [])].filter(Boolean);
  const current = images[idx] || images[0];
  const ytId = extractYoutubeId(p.youtube_url);
  const currency = s?.shop_currency_symbol || "€";

  const currentPriceCents = activeVariant?.price_cents || p.price_cents;
  const currentCompareCents = activeVariant?.compare_at_price_cents || p.compare_at_price_cents;
  const currentStock = activeVariant ? activeVariant.stock_qty : p.stock_qty;
  const stockTracking = p.stock_tracking !== false;
  const inStock = isVariable ? (activeVariant && (!stockTracking || currentStock > 0)) : (!stockTracking || currentStock > 0);
  const lowStock = stockTracking && currentStock > 0 && currentStock <= (s?.shop_low_stock_threshold || 3);
  const discountPct = currentCompareCents > currentPriceCents
    ? Math.round(((currentCompareCents - currentPriceCents) / currentCompareCents) * 100) : 0;

  // Image : variante → fallback galerie
  const variantImage = activeVariant?.image;
  const displayImage = variantImage || current;

  return (
    <>
      <title>{`${p.seo_title || p.name}${s?.company_name ? " — " + s.company_name : ""}`}</title>
      <meta name="description" content={p.seo_description || p.short_description || ""} />

      <section className="pt-8 pb-16 bg-[hsl(var(--muted))]/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)] mb-6">
            <ArrowLeft className="w-4 h-4" />Retour à la boutique
          </Link>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Media */}
            <div className="space-y-3" data-testid="product-media">
              <div className="aspect-square bg-white rounded-2xl border overflow-hidden relative group">
                {showVideo && ytId ? (
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                    allowFullScreen
                    title={p.name}
                  />
                ) : displayImage ? (
                  <img src={displayImage} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Package className="w-20 h-20 text-[hsl(var(--muted-foreground))]/20" /></div>
                )}
                {images.length > 1 && !showVideo && (
                  <>
                    <button
                      type="button"
                      onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid="product-carousel-prev"
                    ><ChevronLeft className="w-5 h-5" /></button>
                    <button
                      type="button"
                      onClick={() => setIdx((i) => (i + 1) % images.length)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid="product-carousel-next"
                    ><ChevronRight className="w-5 h-5" /></button>
                  </>
                )}
                {p.is_featured && !showVideo && (
                  <span className="absolute top-3 left-3 bg-[color:var(--brand-red)] text-white px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold flex items-center gap-1">
                    <Star className="w-3 h-3" fill="white" />Top
                  </span>
                )}
                {discountPct > 0 && !showVideo && (
                  <span className="absolute top-3 right-3 bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                    -{discountPct}%
                  </span>
                )}
              </div>

              {/* Thumbnails */}
              {(images.length > 1 || ytId) && (
                <div className="grid grid-cols-6 gap-2">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => { setIdx(i); setShowVideo(false); }}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${!showVideo && idx === i ? "border-[color:var(--brand-sky)]" : "border-transparent opacity-70 hover:opacity-100"}`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                  {ytId && (
                    <button
                      type="button"
                      onClick={() => setShowVideo(true)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all relative ${showVideo ? "border-[color:var(--brand-sky)]" : "border-transparent opacity-70 hover:opacity-100"}`}
                      data-testid="product-video-thumb"
                    >
                      <img src={`https://i.ytimg.com/vi/${ytId}/default.jpg`} alt="Vidéo" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <Play className="w-5 h-5 text-white" fill="white" />
                      </div>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Info */}
            <div>
              <h1 className="font-display text-3xl md:text-4xl font-black text-[color:var(--brand-navy)] leading-tight">{p.name}</h1>
              {p.short_description && (
                <p className="mt-3 text-lg text-[hsl(var(--muted-foreground))]">{p.short_description}</p>
              )}

              <div className="mt-6 flex items-baseline gap-3">
                {discountPct > 0 ? (
                  <>
                    <span className="text-3xl font-bold text-[color:var(--brand-red)]">{priceFmt(currentPriceCents, currency)}</span>
                    <span className="text-lg line-through text-[hsl(var(--muted-foreground))]">{priceFmt(currentCompareCents, currency)}</span>
                  </>
                ) : (
                  <span className="text-3xl font-bold text-[color:var(--brand-navy)]">{priceFmt(currentPriceCents, currency)}</span>
                )}
                {p.price_per_kg ? (
                  <span className="text-sm font-bold uppercase tracking-wider text-[color:var(--brand-sky)]" data-testid="price-per-kg-badge">/ kg</span>
                ) : s?.shop_tax_included && (
                  <span className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">TTC</span>
                )}
              </div>
              {p.price_per_kg && p.unit_weight_g && (
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">
                  Poids moyen : {p.unit_weight_g}g — estimation : <strong>{priceFmt(Math.round(currentPriceCents * p.unit_weight_g / 1000), currency)}</strong> / unité
                </div>
              )}
              {/* Réglementation alimentaire FR/UE — afficher le prix au kilo pour tout produit alimentaire avec poids */}
              {!p.price_per_kg && p.is_food && p.unit_weight_g > 0 && currentPriceCents > 0 && (
                <div className="text-[12px] text-[hsl(var(--muted-foreground))] mt-1" data-testid="unit-price-disclosure">
                  Soit <strong>{priceFmt(Math.round(currentPriceCents / p.unit_weight_g * 1000), currency)} / kg</strong> · poids net : {p.unit_weight_g} g
                </div>
              )}

              {/* Variant selectors */}
              {isVariable && (p.option_axes || []).length > 0 && (
                <div className="mt-6 space-y-4" data-testid="product-variant-selectors">
                  {(p.option_axes || []).map((axis, ai) => {
                    const selected = selectedOptions[axis.name];
                    // Values disponibles en fonction des autres axes sélectionnés
                    const availableValues = (axis.values || []).map(value => {
                      const candidate = { ...selectedOptions, [axis.name]: value };
                      const match = (p.variants || []).find(v => {
                        const opts = v.options || {};
                        return Object.keys(opts).every(k => opts[k] === candidate[k]) && v.is_active !== false;
                      });
                      return {
                        value,
                        available: !!match,
                        stock: match?.stock_qty || 0,
                        image: match?.image,
                      };
                    });
                    return (
                      <div key={ai}>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] mb-2">
                          {axis.name}
                          {selected && <span className="ml-2 text-[color:var(--brand-navy)] normal-case tracking-normal font-semibold text-xs">: {selected}</span>}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {availableValues.map(({ value, available, stock }) => {
                            const isSel = selected === value;
                            const outOfStock = stockTracking && stock === 0;
                            return (
                              <button
                                key={value}
                                type="button"
                                onClick={() => setSelectedOptions({ ...selectedOptions, [axis.name]: value })}
                                disabled={!available}
                                className={`px-4 h-10 rounded-lg border-2 text-sm font-semibold transition ${
                                  isSel
                                    ? "border-[color:var(--brand-navy)] bg-[color:var(--brand-navy)] text-white"
                                    : available
                                      ? "border-slate-200 hover:border-[color:var(--brand-navy)] text-[color:var(--brand-navy)] bg-white"
                                      : "border-slate-100 text-slate-300 line-through cursor-not-allowed bg-slate-50"
                                } ${outOfStock && !isSel ? "opacity-50" : ""}`}
                                data-testid={`variant-opt-${axis.name}-${value}`}
                              >
                                {value}
                                {outOfStock && available && <span className="ml-1 text-[9px] font-normal opacity-70">(rupture)</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {activeVariant?.sku && (
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] font-mono">SKU : {activeVariant.sku}</div>
                  )}
                </div>
              )}

              {/* Stock badge */}
              {stockTracking && s?.shop_show_stock && (
                <div className="mt-3 flex items-center gap-2" data-testid="product-stock">
                  {inStock ? (
                    <>
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                      <span className={`text-sm font-semibold ${lowStock ? "text-amber-600" : "text-emerald-700"}`}>
                        {lowStock ? `Plus que ${currentStock} en stock — commandez vite` : "En stock"}
                      </span>
                    </>
                  ) : (
                    <span className="text-sm font-semibold text-red-600 flex items-center gap-1.5"><AlertCircle className="w-4 h-4" />{isVariable ? "Variante indisponible" : "Rupture de stock"}</span>
                  )}
                </div>
              )}

              {/* Indicators */}
              {(p.indicators || []).length > 0 && (
                <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="product-indicators">
                  {p.indicators.map((ind, i) => (
                    <div key={i} className="flex items-start gap-2.5 p-3 rounded-xl border bg-white">
                      <Icon name={ind.icon || "Info"} className="w-5 h-5 text-[color:var(--brand-sky)] flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">{ind.label}</div>
                        <div className="text-sm font-semibold text-[color:var(--brand-navy)]">{ind.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Wishlist boost banner — offre ciblée si le produit est en favori depuis X jours */}
              {(() => {
                if (!p) return null;
                const days = daysSinceFavorited(p.id);
                const threshold = s?.wishlist_discount_after_days ?? 7;
                const percent = s?.wishlist_discount_percent ?? 10;
                if (!s?.wishlist_enabled || days === null || days < threshold || percent <= 0) return null;
                const txt = (s?.wishlist_discount_text || "Offre spéciale pour vous : -{percent}% sur ce produit favori !").replace("{percent}", percent);
                return (
                  <div className="mt-4 p-4 rounded-xl bg-gradient-to-br from-[color:var(--brand-red)]/10 to-[color:var(--brand-sky)]/10 border-2 border-[color:var(--brand-red)]/30 flex items-center gap-3" data-testid="wishlist-boost-banner">
                    <div className="w-10 h-10 shrink-0 rounded-full bg-[color:var(--brand-red)] text-white flex items-center justify-center">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-[color:var(--brand-navy)] text-sm">{txt}</div>
                      <div className="text-xs text-[hsl(var(--muted-foreground))]">Dans vos favoris depuis {days} jour{days > 1 ? "s" : ""} — ne passez pas à côté.</div>
                    </div>
                  </div>
                );
              })()}

              {/* Actions */}
              <div className="mt-6 flex items-center gap-3 flex-wrap">
                {/* Qty selector */}
                {inStock && (
                  <div className="flex items-center gap-1 border rounded-full h-14 px-2 bg-white" data-testid="product-qty-selector">
                    <button
                      type="button"
                      onClick={() => setQty(Math.max(1, qty - 1))}
                      disabled={qty <= 1}
                      className="w-9 h-9 rounded-full hover:bg-[hsl(var(--muted))] disabled:opacity-30 flex items-center justify-center transition"
                      aria-label="Moins"
                    >−</button>
                    <span className="min-w-[2.2rem] text-center font-bold tabular-nums" data-testid="product-qty-value">{qty}</span>
                    <button
                      type="button"
                      onClick={() => setQty(Math.min(stockTracking ? currentStock : 99, qty + 1))}
                      disabled={stockTracking && qty >= currentStock}
                      className="w-9 h-9 rounded-full hover:bg-[hsl(var(--muted))] disabled:opacity-30 flex items-center justify-center transition"
                      aria-label="Plus"
                    >+</button>
                  </div>
                )}
                <button
                  type="button"
                  disabled={!inStock}
                  className="flex-1 min-w-[200px] h-14 rounded-full bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/90 text-white font-bold text-base inline-flex items-center justify-center gap-2 transition disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="product-add-to-cart"
                  onClick={() => {
                    addToCart(p, qty, activeVariant);
                    const variantLabel = activeVariant ? ` (${Object.values(activeVariant.options || {}).join(" · ")})` : "";
                    toast.success(`${p.name}${variantLabel} ajouté au panier · ${qty} × ${priceFmt(currentPriceCents, currency)}`);
                  }}
                >
                  <ShoppingCart className="w-5 h-5" />Ajouter au panier
                </button>
                {s?.shop_click_collect_enabled && (
                  <button
                    type="button"
                    disabled={!inStock}
                    className="h-14 px-6 rounded-full bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy)]/90 text-white font-bold text-base inline-flex items-center gap-2 transition disabled:opacity-40"
                    data-testid="product-click-collect"
                  >
                    Click & Collect
                  </button>
                )}
              </div>

              {/* SKU */}
              {p.sku && (
                <div className="mt-5 text-[11px] text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
                  Réf. : <span className="font-mono">{p.sku}</span>
                </div>
              )}
            </div>
          </div>

          {/* Long description */}
          {p.long_description && (
            <div className="mt-12 prose max-w-none">
              <h2 className="font-display text-2xl font-bold text-[color:var(--brand-navy)]">Description</h2>
              <div className="mt-4 text-[hsl(var(--muted-foreground))]" dangerouslySetInnerHTML={{ __html: p.long_description }} />
            </div>
          )}

          {/* Food block (Phase 14) */}
          {p.is_food && (
            <FoodInfoBlock product={p} />
          )}

          {/* Usage tips */}
          {p.usage_tips && (
            <div className="mt-10 p-6 rounded-2xl bg-[color:var(--brand-sky)]/5 border border-[color:var(--brand-sky)]/10">
              <h2 className="font-display text-xl font-bold text-[color:var(--brand-navy)] flex items-center gap-2">
                <Icon name="Lightbulb" className="w-5 h-5 text-[color:var(--brand-sky)]" />
                Conseils d'utilisation
              </h2>
              <div className="mt-3 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed" dangerouslySetInnerHTML={{ __html: p.usage_tips }} />
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function FoodInfoBlock({ product }) {
  const [allergens, setAllergens] = React.useState([]);
  React.useEffect(() => {
    import("../../lib/api").then(({ api }) => api.get("/shop/meta/allergens").then(r => setAllergens(r.data || [])).catch(() => {}));
  }, []);
  const decl = allergens.filter(a => (product.allergens || []).includes(a.code));
  // Surligne les allergènes dans les ingrédients
  let ingredientsHtml = (product.ingredients || "").replace(/[<>&]/g, c => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));
  for (const a of decl) {
    const regex = new RegExp(`\\b(${a.label.split(/[ (]/)[0]}\\w*)\\b`, "gi");
    ingredientsHtml = ingredientsHtml.replace(regex, '<strong class="text-amber-800 bg-amber-100 px-0.5 rounded">$1</strong>');
  }
  return (
    <div className="mt-10 p-6 rounded-2xl bg-emerald-50/40 border-2 border-emerald-200" data-testid="food-info-block">
      <h2 className="font-display text-xl font-bold text-[color:var(--brand-navy)] flex items-center gap-2">
        <Icon name="Apple" className="w-5 h-5 text-emerald-600" />
        Informations produit alimentaire
      </h2>

      {product.ingredients && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-1">Ingrédients</div>
          <p className="text-sm text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: ingredientsHtml }} />
        </div>
      )}

      {decl.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mb-2">⚠️ Contient {decl.length} allergène(s)</div>
          <div className="flex flex-wrap gap-1.5">
            {decl.map(a => (
              <span key={a.code} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 border border-amber-300 text-xs font-bold text-amber-900" data-testid={`food-allergen-${a.code}`}>
                <span className="text-sm leading-none">{a.icon}</span> {a.label}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        {product.origin && (
          <div className="p-3 rounded-xl bg-white border">
            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Origine</div>
            <div className="text-sm font-bold text-[color:var(--brand-navy)] mt-0.5">{product.origin}</div>
          </div>
        )}
        {product.dlc_days && (
          <div className="p-3 rounded-xl bg-white border">
            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">DLC</div>
            <div className="text-sm font-bold text-[color:var(--brand-navy)] mt-0.5">{product.dlc_days} jour{product.dlc_days > 1 ? "s" : ""}</div>
          </div>
        )}
        {product.conservation && (
          <div className="p-3 rounded-xl bg-white border col-span-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Conservation</div>
            <div className="text-sm font-bold text-[color:var(--brand-navy)] mt-0.5">{product.conservation}</div>
          </div>
        )}
      </div>
    </div>
  );
}

